import { NzUploadFile } from 'ng-zorro-antd/upload';
import { ApiResponse } from '@nano/core/api';
import { PaymentAdditionalInfo } from '@nano/payment-additional-info';
import { PaymentServiceType, PaymentSystem } from '@nano/payment';

export const PaymentEntryL10nKeys = {
  operationType: (type: string): string => `payment.details.operationType.${type}`,
  reasonCode: (code: string): string => `payment.details.reasonCode.${code}`,
  paymentStage: (status: string): string => `alert.paymentStage.${status}`,
  paymentCode: 'payment.details.paymentCode',
  exchangeRate: 'payment.exchangeRate.template',
  incomeWaiting: 'payment.list.tooltip.incomeWaiting'
};

export enum PaymentDirection {
  DEBIT = 'DEBIT',
  CREDIT = 'CREDIT'
}

export enum PaymentEntryType {
  INTERNAL = 'INTERNAL',
  INTERNAL_ROLLBACK = 'INTERNAL_ROLLBACK',
  CURRENCY_EXCHANGE = 'CURRENCY_EXCHANGE',
  PURCHASE = 'PURCHASE',
  REVERSAL = 'REVERSAL',
  REFUND = 'REFUND',
  TOPUP = 'TOPUP',
  CARRIER_BILLING_TOP_UP = 'CARRIER_BILLING_TOP_UP',
  INTERBANK = 'INTERBANK',
  PAYMENT_REQUEST = 'PAYMENT_REQUEST',
  ONBOARDING = 'ONBOARDING',
  ONBOARDING_ROLLBACK = 'ONBOARDING_ROLLBACK',
  INTERNAL_TIPS = 'INTERNAL_TIPS',
  INTERNAL_TIPS_ROLLBACK = 'INTERNAL_TIPS_ROLLBACK',
  ONBOARDING_TIPS = 'ONBOARDING_TIPS',
  ONBOARDING_TIPS_ROLLBACK = 'ONBOARDING_TIPS_ROLLBACK',
  FEE = 'FEE',
  DEBT = 'DEBT',
  SUPPLEMENTARY_SERVICE = 'SUPPLEMENTARY_SERVICE',
  INTEREST_REPAYMENT = 'INTEREST_REPAYMENT',
  INTEREST_PAYOUT = 'INTEREST_PAYOUT',
  FUNDS_TRANSFER = 'FUNDS_TRANSFER',
  BLOCK = 'BLOCK',
  INTERNAL_TRANSFER = 'INTERNAL_TRANSFER'
}

export enum LoanType {
  LOAN_DISBURSEMENT = 'LOAN_DISBURSEMENT',
  LOAN_PRINCIPAL_REPAYMENT = 'LOAN_PRINCIPAL_REPAYMENT',
  LOAN_INTEREST_REPAYMENT = 'LOAN_INTEREST_REPAYMENT',
  LOAN_OBLIGATION_FEE_REPAYMENT = 'LOAN_OBLIGATION_FEE_REPAYMENT',
  LOAN_PRINCIPAL_OVERDUE_REPAYMENT = 'LOAN_PRINCIPAL_OVERDUE_REPAYMENT',
  LOAN_INTEREST_OVERDUE_REPAYMENT = 'LOAN_INTEREST_OVERDUE_REPAYMENT',
  LOAN_PRINCIPAL_OVERDUE_INTEREST_REPAYMENT = 'LOAN_PRINCIPAL_OVERDUE_INTEREST_REPAYMENT',
  LOAN_OBLIGATION_FEE_OVERDUE_REPAYMENT = 'LOAN_OBLIGATION_FEE_OVERDUE_REPAYMENT',
  LOAN_PRINCIPAL_PENALTY_REPAYMENT = 'LOAN_PRINCIPAL_PENALTY_REPAYMENT',
  LOAN_INTEREST_PENALTY_REPAYMENT = 'LOAN_INTEREST_PENALTY_REPAYMENT',
  LOAN_OBLIGATION_FEE_PENALTY_REPAYMENT = 'LOAN_OBLIGATION_FEE_PENALTY_REPAYMENT'
}

export enum OverdraftType {
  OVERDRAFT_COVERAGE = 'OVERDRAFT_COVERAGE',
  OVERDRAFT_USE = 'OVERDRAFT_USE',
  OVERDRAFT_USE_FEE = 'OVERDRAFT_USE_FEE'
}

export enum CardType {
  CARD_PURCHASE = 'CARD_PURCHASE',
  CARD_PURCHASE_REVERSAL = 'CARD_PURCHASE_REVERSAL',
  CARD_PURCHASE_WITH_CASHBACK = 'CARD_PURCHASE_WITH_CASHBACK',
  CARD_PURCHASE_WITH_CASHBACK_REVERSAL = 'CARD_PURCHASE_WITH_CASHBACK_REVERSAL',
  CARD_PURCHASE_UNIQUE = 'CARD_PURCHASE_UNIQUE',
  CARD_PURCHASE_UNIQUE_REVERSAL = 'CARD_PURCHASE_UNIQUE_REVERSAL',
  CARD_REFUND = 'CARD_REFUND',
  CARD_REFUND_REVERSAL = 'CARD_REFUND_REVERSAL',
  CARD_CASH_WITHDRAWAL = 'CARD_CASH_WITHDRAWAL',
  CARD_CASH_WITHDRAWAL_REVERSAL = 'CARD_CASH_WITHDRAWAL_REVERSAL',
  CARD_CASH_WITHDRAWAL_POS = 'CARD_CASH_WITHDRAWAL_POS',
  CARD_CASH_WITHDRAWAL_POS_REVERSAL = 'CARD_CASH_WITHDRAWAL_POS_REVERSAL',
  CARD_CASH_ADVANCE = 'CARD_CASH_ADVANCE',
  CARD_CASH_ADVANCE_REVERSAL = 'CARD_CASH_ADVANCE_REVERSAL',
  CARD_CASH_IN = 'CARD_CASH_IN',
  CARD_CASH_IN_REVERSAL = 'CARD_CASH_IN_REVERSAL',
  CARD_ADJUSTMENT_CREDIT = 'CARD_ADJUSTMENT_CREDIT',
  CARD_ADJUSTMENT_CREDIT_REVERSAL = 'CARD_ADJUSTMENT_CREDIT_REVERSAL',
  CARD_ADJUSTMENT_DEBIT = 'CARD_ADJUSTMENT_DEBIT',
  CARD_ADJUSTMENT_DEBIT_REVERSAL = 'CARD_ADJUSTMENT_DEBIT_REVERSAL',
  CARD_PAYMENT_CREDIT = 'CARD_PAYMENT_CREDIT',
  CARD_PAYMENT_CREDIT_REVERSAL = 'CARD_PAYMENT_CREDIT_REVERSAL',
  CARD_ACCOUNT_FUNDING_TRANSACTION = 'CARD_ACCOUNT_FUNDING_TRANSACTION',
  CARD_ACCOUNT_FUNDING_TRANSACTION_REVERSAL = 'CARD_ACCOUNT_FUNDING_TRANSACTION_REVERSAL'
}

export enum PaymentStatus {
  // Reserved
  AUTHORIZED = 'AUTHORIZED',
  // Pending
  PENDING = 'PENDING',
  INITIATED = 'INITIATED',
  CONFIRMED = 'CONFIRMED',
  PROCESSING = 'PROCESSING',
  WAITING = 'WAITING',
  ACTIVE = 'ACTIVE',
  // if amlStatusUnderReviewEnabled, then Under Review, otherwise - Pending
  VERIFICATION = 'VERIFICATION',
  SUSPENDED = 'SUSPENDED',
  // Completed
  COMPLETED = 'COMPLETED',
  // Declined
  REJECTED = 'REJECTED',
  CANCELED = 'CANCELED',
  FAILED = 'FAILED',
  REMOVED = 'REMOVED',
  // Reversed
  REVERSED = 'REVERSED',
  // Wait for funds
  WAIT_FOR_FUNDS = 'WAIT_FOR_FUNDS'
}

export const PaymentFinalStatuses = [
  PaymentStatus.COMPLETED,
  PaymentStatus.REJECTED,
  PaymentStatus.CANCELED,
  PaymentStatus.FAILED,
  PaymentStatus.REMOVED,
  PaymentStatus.REVERSED,
  PaymentStatus.INITIATED
];

export const isPaymentAmountReserved = (status: PaymentStatus): boolean => status === PaymentStatus.AUTHORIZED;
export const isPaymentCompleted = (status: PaymentStatus): boolean => status === PaymentStatus.COMPLETED;
export const isPaymentInitiated = (status: PaymentStatus): boolean => status === PaymentStatus.INITIATED;
export const isPaymentFailed = (status: PaymentStatus): boolean => status === PaymentStatus.FAILED;
export const isPaymentCanceled = (status: PaymentStatus): boolean => status === PaymentStatus.CANCELED;
export const isPaymentStatusFinal = (status: PaymentStatus): boolean => PaymentFinalStatuses.includes(status);

export enum PaymentTypeUI {
  INCOMING = 'incoming',
  OUTGOING = 'outgoing',
  BLOCKED = 'blocked'
}

export const PaymentReservedStatuses: PaymentStatus[] = [PaymentStatus.AUTHORIZED];
export const PaymentCompletedStatuses: PaymentStatus[] = [PaymentStatus.COMPLETED];
export const PaymentPendingStatuses: PaymentStatus[] = [
  PaymentStatus.PENDING,
  PaymentStatus.CONFIRMED,
  PaymentStatus.PROCESSING,
  PaymentStatus.VERIFICATION,
  PaymentStatus.SUSPENDED,
  PaymentStatus.ACTIVE,
  PaymentStatus.WAITING
];
export const PaymentDeclinedStatuses: PaymentStatus[] = [PaymentStatus.REJECTED, PaymentStatus.CANCELED, PaymentStatus.FAILED, PaymentStatus.REMOVED];
export const PaymentReversedStatuses: PaymentStatus[] = [PaymentStatus.REVERSED];
export const PaymentInitiatedStatuses: PaymentStatus[] = [PaymentStatus.INITIATED];
export const PaymentWaitForFundsStatuses: PaymentStatus[] = [PaymentStatus.WAIT_FOR_FUNDS];
export const PaymentUnderReviewStatuses: PaymentStatus[] = [PaymentStatus.VERIFICATION, PaymentStatus.SUSPENDED];
export const PaymentPendingStatusesWithoutReview: PaymentStatus[] = [
  PaymentStatus.PENDING,
  PaymentStatus.CONFIRMED,
  PaymentStatus.PROCESSING,
  PaymentStatus.ACTIVE,
  PaymentStatus.WAITING
];

export const isIbOperation = (paymentEntry: PaymentEntry): boolean => !!paymentEntry.applicantPartyName;
export const isDebitOperation = (direction: PaymentDirection): boolean => direction === PaymentDirection.DEBIT;
export const isCardOperation = (operationType: string): boolean => operationType in CardType;
export const isCurrencyExchangeOperation = (operationType: PaymentEntryType): boolean => operationType === PaymentEntryType.CURRENCY_EXCHANGE;
export const isLoanOperation = (operationType: string): boolean => operationType in LoanType;
export const isOverdraftOperation = (operationType: string): boolean => operationType in OverdraftType;
export const isDebtOperation = (operationType: PaymentEntryType): boolean => operationType === PaymentEntryType.DEBT;
export const isFeeOperation = (operationType: PaymentEntryType): boolean => operationType === PaymentEntryType.FEE;
export const isBlockOperation = (operationType: PaymentEntryType): boolean => operationType === PaymentEntryType.BLOCK;
export const isInternalRollbackOperation = (operationType: PaymentEntryType): boolean => operationType === PaymentEntryType.INTERNAL_ROLLBACK;
export const isOperationWaiting = (status: PaymentStatus): boolean => status === PaymentStatus.WAITING;
export const isReversedIbOperation = (paymentEntry: PaymentEntry): boolean => isIbOperation(paymentEntry) && paymentEntry.status === PaymentStatus.REVERSED;
export const isFeeOperationInitiatedByLoanAgreement = (paymentEntry: PaymentEntry): boolean =>
  paymentEntry.operationType == PaymentEntryType.FEE && paymentEntry.initiatorType == 'LOAN_AGREEMENT';

export const getBeneficiaryOrPayerName = (payment: PaymentEntry, bankName: string): string => {
  if (isDebitOperation(payment.direction)) {
    if (isBlockOperation(payment.operationType)) {
      return payment.initiator || bankName;
    } else {
      return payment.beneficiaryName || payment.merchantName || payment.otherName || bankName;
    }
  } else {
    return payment.payerName || payment.merchantName || bankName;
  }
};
export interface PaymentEntry extends PaymentAdditionalInfo {
  paymentSystem: PaymentSystem;
  targetPaymentSystem?: string;
  operationId: string;
  initiatorId: string;
  serviceId: string;
  operationType: PaymentEntryType;
  serviceType: string;
  direction: PaymentDirection;
  beneficiaryBank?: string;
  beneficiaryName: string;
  beneficiaryAccountNumber: string;
  beneficiaryPhoneNumber?: string;
  beneficiaryBankName?: string;
  beneficiaryBankIdentifier?: string;
  beneficiaryBankAddress?: string;
  beneficiaryBankCountry?: string;
  instructionIdentification?: string;
  payerName?: string;
  payerAccountNumber: string;
  payerPhoneNumber?: string;
  payerBankIdentifier?: string;
  payerBankName?: string;
  amount: number;
  currency: string;
  remainingAmount?: number;
  remainingCurrency?: string;
  otherName?: string;
  otherAmount?: number;
  otherCurrency?: string;
  originalOtherAmount?: number;
  initialAmount?: number;
  initialCurrency?: string;
  exchangeRate?: number;
  exchangeRateCurrencyPair?: { baseCurrency: string; quoteCurrency: string };
  date: string;
  sortDate?: string;
  createdOn: string;
  updatedOn?: string;
  modifiedOn?: string;
  downloadable: boolean;
  paymentDetails: string;
  paymentCode?: string;
  status: PaymentStatus;
  reasonCode?: string;
  reasonMessage?: string;

  originalConfirmedOn: string;
  originalBeneficiaryName: string;
  originalBeneficiaryAccountNumber: string;
  originalPaymentDetails: string;

  feeAmount: number;
  feeCurrency: string;
  feeAccountNumber?: string;
  serviceChargeDetails?: string;
  tieredFeesApplied: boolean;

  initialFeeAmount?: number;
  initialFeeCurrency?: string;

  transactionAmount: number;
  transactionCurrency: string;
  merchantName: string;
  merchantAddress: string;
  merchantCity: string;
  merchantCountry: string;
  merchantCategoryCode: string;

  applicantPartyName: string;
  confirmedPartyName: string;
  confirmedOn: string;

  cardProductType?: string;
  cardPaymentNetwork?: string;
  pan?: string;
  cardPcId?: string;

  initiator?: string;
  initiatorType?: string;
  agreementId?: string;

  documentId?: string;
  availableSpendingAmount?: number;
  payerAccountIsViewOnly?: boolean;

  waitForFunds?: boolean;

  attachments?: NzUploadFile[];
}

export const getPaymentModifiedOn = (payment: Pick<PaymentEntry, 'modifiedOn' | 'updatedOn'>): string | undefined => payment.modifiedOn || payment.updatedOn;

export interface PaymentEntryResponse extends PaymentEntry, ApiResponse {}

export enum PaymentEntryGroup {
  day = 'day',
  week = 'week',
  month = 'month',
  year = 'year',
  other = 'other'
}

export const isPaymentRepeatable = (payment: PaymentEntry): boolean =>
  isDebitOperation(payment.direction) &&
  !payment.payerAccountIsViewOnly &&
  [PaymentEntryType.INTERNAL, PaymentEntryType.INTERBANK].includes(payment.operationType) &&
  (payment.serviceType as PaymentServiceType) !== PaymentServiceType.OUTGOING_PAYMENT_RETURN;
